import { useCallback, useMemo, useState } from 'react'

import { Text, Primary13Regular, Flex } from '@sequencehq/core-components'
import { format } from '@sequencehq/utils/dates'
import { AggregatedCreditNote } from 'CreditNotes/types'
import { SummaryRow } from './SummaryRow'
import { SummaryRowEditButton } from './SummaryRowEditButton'
import { usePermissions } from 'lib/rbac'
import { CreditNoteAccountingDatePicker } from './CreditNoteAccountingDatePicker'

type Props = {
  updateAccountingDate: (
    accountingDate: AggregatedCreditNote['accountingDate']
  ) => void
  accountingDate?: AggregatedCreditNote['accountingDate']
  status: AggregatedCreditNote['status']
}

export const CreditNoteAccountingDate = ({
  updateAccountingDate,
  accountingDate,
  status
}: Props) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false)

  const formattedAccountingDate = useMemo(() => {
    if (!accountingDate) {
      return '-'
    }
    return format(new Date(accountingDate), 'd MMM yyyy')
  }, [accountingDate])

  const handleChange = useCallback(
    (newAccountingDate: string) => {
      updateAccountingDate(newAccountingDate)
      setIsPickerOpen(false)
    },
    [updateAccountingDate]
  )

  const { can } = usePermissions()

  const datePickerComponent = useMemo(() => {
    if (status !== 'DRAFT' || !can('org.creditNote:update')) {
      return null
    }

    return (
      <SummaryRowEditButton
        aria-label="Edit accounting date"
        data-testid="creditNote.accountingDate.edit"
        onClick={() => setIsPickerOpen(true)}
      />
    )
  }, [status, can])

  return (
    <SummaryRow
      label="Accounting date"
      value={
        <Flex alignItems="center" gap={1} role="group">
          {isPickerOpen ? (
            <CreditNoteAccountingDatePicker
              accountingDate={accountingDate}
              onChange={handleChange}
              openOnMount={true}
              data-testid="creditNote.accountingDate.picker"
            />
          ) : (
            <>
              <Text
                {...Primary13Regular}
                data-testid="creditNote.accountingDate.readOnly"
              >
                {formattedAccountingDate}
              </Text>
              {datePickerComponent}
            </>
          )}
        </Flex>
      }
    />
  )
}
